/**
 * Late status reports: a report submitted two or more days after the month's
 * due date. Days are London calendar days, so "due Monday, in on Wednesday"
 * is late whatever the hour, and a report in the day after the due date is
 * not - that one day is the grace.
 *
 * The month's due date is the only bar. An extension moves a project's lock,
 * not its due date, so it never excuses lateness; and a month with no due
 * date cannot have a late report, which is the server's rule too.
 */
import type { MonthReview, Project, ProjectReport } from '../types'
import { londonDate, parseInstant } from './reportingWindow'

export const LATE_AFTER_DAYS = 2

export type ReportTiming =
  /** In before the due date or within the day after it. */
  | 'on_time'
  /** In LATE_AFTER_DAYS or more days after the due date. */
  | 'late'
  /** Nothing submitted and the due date has passed. */
  | 'not_filed'
  /** Nothing submitted, due date still ahead or none set. */
  | 'pending'

export interface ReportStanding {
  project: Project
  report?: ProjectReport
  timing: ReportTiming
  /** London calendar days from the due date to the submission, 0 when in on
   *  the day or early. Null when there is nothing to compare. */
  daysAfterDue: number | null
}

export interface MonthStanding {
  month: string
  dueAt: string | null
  rows: ReportStanding[]
  onTime: number
  late: number
  notFiled: number
  pending: number
}

const DAY_MS = 24 * 60 * 60 * 1000

const utcDay = (isoDate: string) =>
  Date.UTC(Number(isoDate.slice(0, 4)), Number(isoDate.slice(5, 7)) - 1, Number(isoDate.slice(8, 10)))

/** Whole London calendar days from one instant to another, sign kept. */
export function daysBetweenLondon(from: string, to: string): number {
  return Math.round((utcDay(londonDate(to)) - utcDay(londonDate(from))) / DAY_MS)
}

export function reportTiming(
  report: ProjectReport | undefined,
  dueAt: string | null,
  now: Date,
): { timing: ReportTiming; daysAfterDue: number | null } {
  // A draft has no submission, and reopening one clears the stamp it had.
  const submittedAt = report && report.state !== 'draft' ? report.submittedAt : undefined
  if (!submittedAt) {
    const overdue = dueAt !== null && now.getTime() > parseInstant(dueAt).getTime()
    return { timing: overdue ? 'not_filed' : 'pending', daysAfterDue: null }
  }
  if (!dueAt) return { timing: 'on_time', daysAfterDue: null }
  const days = Math.max(0, daysBetweenLondon(dueAt, submittedAt))
  return { timing: days >= LATE_AFTER_DAYS ? 'late' : 'on_time', daysAfterDue: days }
}

const TIMING_ORDER: Record<ReportTiming, number> = { late: 0, not_filed: 1, on_time: 2, pending: 3 }

/**
 * How every project that owed the month a report fared. Who owed one: the
 * projects opted into reporting that are in flight, plus anyone who filed, so
 * a project completed since is still judged for the months it reported. A
 * past month also keeps the projects validated back then, the way the
 * month-end table does.
 */
export function monthStanding(
  month: string,
  projects: Project[],
  review: MonthReview | null | undefined,
  reports: ProjectReport[],
  now: Date,
  isCurrentMonth: boolean,
): MonthStanding {
  const byId = new Map(projects.map(p => [p.id, p]))
  const filed = new Map(reports.map(r => [r.projectId, r]))

  const owed = new Set<string>()
  for (const p of projects) if (p.reportable && p.status === 'forecast') owed.add(p.id)
  if (!isCurrentMonth) {
    for (const id of Object.keys(review?.reviews ?? {})) if (byId.get(id)?.reportable) owed.add(id)
  }
  for (const r of reports) if (byId.has(r.projectId)) owed.add(r.projectId)

  const dueAt = review?.reportingWindow?.dueAt ?? null
  const rows: ReportStanding[] = []
  for (const id of owed) {
    const project = byId.get(id)!
    const report = filed.get(id)
    rows.push({ project, report, ...reportTiming(report, dueAt, now) })
  }
  rows.sort((a, b) =>
    TIMING_ORDER[a.timing] - TIMING_ORDER[b.timing]
    || (b.daysAfterDue ?? 0) - (a.daysAfterDue ?? 0)
    || a.project.name.localeCompare(b.project.name))

  const count = (timing: ReportTiming) => rows.filter(r => r.timing === timing).length
  return {
    month,
    dueAt,
    rows,
    onTime: count('on_time'),
    late: count('late'),
    notFiled: count('not_filed'),
    pending: count('pending'),
  }
}
